// frontend/src/pages/TransactionsPage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { MoreVertical, Trash2, Edit, X, ArrowLeft, ArrowRight } from 'lucide-react';

const TransactionForm = React.memo(({ 
    transactionToEdit, 
    selectedDate, 
    accounts, 
    categories, 
    accountTypes, 
    onFinish, 
    onCancel,
    selectedCurrency,
    formatCurrency 
}) => {
    const isEditing = !!transactionToEdit;
    
    const activeAccounts = useMemo(() => accounts.filter(a => a.status === 'ativo' && a.currency === selectedCurrency), [accounts, selectedCurrency]);

    const [type, setType] = useState(isEditing ? transactionToEdit.type : 'expense');
    const [amount, setAmount] = useState(isEditing ? transactionToEdit.amount : '');
    const [creditAmount, setCreditAmount] = useState(isEditing ? transactionToEdit.credit_amount || '' : '');
    const [description, setDescription] = useState(isEditing ? transactionToEdit.description : '');
    const [accountId, setAccountId] = useState(isEditing ? transactionToEdit.account_id : '');
    const [toAccountId, setToAccountId] = useState(isEditing ? transactionToEdit.to_account_id || '' : '');
    const [categoryId, setCategoryId] = useState('');
    const [date, setDate] = useState(isEditing ? transactionToEdit.date.split('T')[0] : selectedDate.toISOString().split('T')[0]);
    const [error, setError] = useState('');
    const { apiFetch } = useApi();

    const creditCardAccountTypes = useMemo(() => {
        const creditCardType = accountTypes.find(at => at.name.toLowerCase().includes('crédito'));
        return creditCardType ? creditCardType.id : null;
    }, [accountTypes]);

    const creditCardAccounts = useMemo(() => activeAccounts.filter(a => a.account_type_id === creditCardAccountTypes), [activeAccounts, creditCardAccountTypes]);
    
    const otherAccounts = useMemo(() => activeAccounts.filter(a => a.account_type_id !== creditCardAccountTypes), [activeAccounts, creditCardAccountTypes]);

    const isTransactionLocked = useMemo(() => {
        if (!isEditing) return false;
        
        // A lógica de bloqueio para pagamentos de fatura é tratada da mesma forma que transferências
        if (transactionToEdit.linked_transaction_id) return true;

        const originalAccount = accounts.find(acc => acc.id === transactionToEdit.account_id);
        if (originalAccount && originalAccount.status === 'inativo') return true;
        
        if (transactionToEdit.to_account_id) {
            const toAccount = accounts.find(acc => acc.id === transactionToEdit.to_account_id);
            if (toAccount && toAccount.status === 'inativo') return true;
        }
        
        const originalCategory = categories.find(cat => cat.name === transactionToEdit.category);
        if (originalCategory && originalCategory.status === 'inativo') return true;

        return false;
    }, [isEditing, transactionToEdit, accounts, categories]);
    
    useEffect(() => {
        if (isEditing) {
            const foundCategory = categories.find(c => c.name === transactionToEdit.category);
            setCategoryId(foundCategory ? foundCategory.id : '');
            return;
        }

        switch (type) {
            case 'transfer':
                if (otherAccounts.length >= 2) {
                    setAccountId(otherAccounts[0].id);
                    setToAccountId(otherAccounts[1].id);
                }
                break;
            case 'bill_payment':
                if (otherAccounts.length > 0) setAccountId(otherAccounts[0].id);
                if (creditCardAccounts.length > 0) setToAccountId(creditCardAccounts[0].id);
                break;
            case 'expense':
            case 'revenue':
            default:
                if (activeAccounts.length > 0) setAccountId(activeAccounts[0].id);
                break;
        }
    }, [type, isEditing, activeAccounts, creditCardAccounts, otherAccounts, categories, transactionToEdit]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if ((type === 'bill_payment' || type === 'transfer') && (!accountId || !toAccountId)) {
            setError('Por favor, selecione a conta de origem e de destino.');
            return;
        }
        if ((type === 'bill_payment' || type === 'transfer') && String(accountId) === String(toAccountId)) {
            setError('A conta de origem e de destino não podem ser a mesma.');
            return;
        }
        
        const payload = {
            description,
            amount: parseFloat(amount) || 0,
            date,
            category: type === 'transfer' ? 'Transferência' : (type === 'bill_payment' ? 'Pagamento de Fatura' : categories.find(c => c.id === parseInt(categoryId))?.name || ''),
            type,
            accountId: parseInt(accountId),
            toAccountId: (type === 'bill_payment' || type === 'transfer') ? parseInt(toAccountId) : null,
            creditAmount: type === 'bill_payment' ? (parseFloat(creditAmount) || parseFloat(amount) || 0) : null,
        };
        
        try {
            if (isEditing) {
                await apiFetch(`/transactions/${transactionToEdit.id}`, { method: 'PUT', body: payload });
            } else {
                await apiFetch('/transactions', { method: 'POST', body: payload });
            }
            onFinish();
        } catch (err) {
            setError(err.message);
        }
    };
    
    const activeRevenueCategories = categories.filter(c => c.type === 'revenue' && c.status === 'ativo' && !['transferência', 'pagamento de fatura'].includes(c.name.toLowerCase()));
    const activeExpenseCategories = categories.filter(c => c.type === 'expense' && c.status === 'ativo' && !['transferência', 'pagamento de fatura'].includes(c.name.toLowerCase()));
    
    const typeClasses = { expense: 'bg-red-50 border-red-200', revenue: 'bg-green-50 border-green-200', transfer: 'bg-yellow-50 border-yellow-200', bill_payment: 'bg-gradient-to-r from-red-50 to-blue-50 border-blue-200' };
    const buttonClasses = { expense: 'bg-red-500 hover:bg-red-600', revenue: 'bg-green-500 hover:bg-green-600', transfer: 'bg-yellow-500 hover:bg-yellow-600', bill_payment: 'bg-blue-600 hover:bg-blue-700' };

    const canTransfer = otherAccounts.length >= 2;
    const canPayBill = creditCardAccounts.length > 0 && otherAccounts.length > 0;

    return (
        <form onSubmit={handleSubmit} className={`p-4 space-y-4 rounded-b-lg border-t-4 transition-all ${typeClasses[type]}`}>
             {error && <p className="p-2 text-sm text-red-700 bg-red-100 rounded">{error}</p>}
             {isTransactionLocked && <div className="p-2 text-sm text-orange-700 bg-orange-100 rounded">Este lançamento não pode ser editado.</div>}

            <fieldset disabled={isTransactionLocked}>
                <div className="flex p-1 space-x-1 bg-gray-200 rounded-lg">
                    <button type="button" onClick={() => setType('expense')} className={`w-full p-2 rounded-md font-semibold transition-colors ${type === 'expense' ? 'bg-white shadow text-red-600' : 'hover:bg-red-100'}`}>Despesa</button>
                    <button type="button" onClick={() => setType('revenue')} className={`w-full p-2 rounded-md font-semibold transition-colors ${type === 'revenue' ? 'bg-white shadow text-green-600' : 'hover:bg-green-100'}`}>Receita</button>
                    {canTransfer && <button type="button" onClick={() => setType('transfer')} className={`w-full p-2 rounded-md font-semibold transition-colors ${type === 'transfer' ? 'bg-white shadow text-yellow-600' : 'hover:bg-yellow-100'}`}>Transferência</button>}
                    {canPayBill && <button type="button" onClick={() => setType('bill_payment')} className={`w-full p-2 rounded-md font-semibold transition-colors ${type === 'bill_payment' ? 'bg-white shadow text-blue-600' : 'hover:bg-blue-100'}`}>Pagar Fatura</button>}
                </div>
                
                {type === 'bill_payment' ? (
                    <>
                        <div><label className="text-sm font-medium">Conta de Origem</label><select value={accountId} onChange={e => setAccountId(e.target.value)} className="w-full p-2 mt-1 border rounded">{otherAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                        <div><label className="text-sm font-medium">Cartão de Crédito (Destino)</label><select value={toAccountId} onChange={e => setToAccountId(e.target.value)} className="w-full p-2 mt-1 border rounded">{creditCardAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                        <div><label className="text-sm font-medium">Valor Pago ({selectedCurrency})</label><input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full p-2 mt-1 border rounded" /></div>
                        <div><label className="text-sm font-medium">Valor a Creditar no Limite ({selectedCurrency})</label><input type="number" step="0.01" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="O mesmo que o valor pago, se em branco" className="w-full p-2 mt-1 border rounded" /></div>
                    </>
                ) : type === 'transfer' ? (
                     <>
                        <div><label className="text-sm font-medium">Conta de Origem</label><select value={accountId} onChange={e => setAccountId(e.target.value)} className="w-full p-2 mt-1 border rounded">{otherAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                        <div><label className="text-sm font-medium">Conta de Destino</label><select value={toAccountId} onChange={e => setToAccountId(e.target.value)} className="w-full p-2 mt-1 border rounded">{otherAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                        <div><label className="text-sm font-medium">Valor ({selectedCurrency})</label><input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full p-2 mt-1 border rounded" /></div>
                    </>
                ) : (
                    <>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="text-sm font-medium">Conta</label>
                                <select value={accountId} onChange={e => setAccountId(e.target.value)} className="w-full p-2 mt-1 border rounded">
                                     {isEditing && !activeAccounts.some(a => a.id === parseInt(accountId)) && (
                                        <option key={accountId} value={accountId}>
                                            {accounts.find(a => a.id === parseInt(accountId))?.name} (Inativa)
                                        </option>
                                    )}
                                    {activeAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div><label className="text-sm font-medium">Valor ({selectedCurrency})</label><input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full p-2 mt-1 border rounded" /></div>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Categoria</label>
                            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className="w-full p-2 mt-1 border rounded">
                                <option value="">Selecione</option>
                                {isEditing && !categories.some(c => c.id === parseInt(categoryId) && c.status === 'ativo') && (
                                     <option key={categoryId} value={categoryId}>
                                        {categories.find(c => c.id === parseInt(categoryId))?.name} (Inativa)
                                    </option>
                                )}
                                {(type === 'expense' ? activeExpenseCategories : activeRevenueCategories).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </>
                )}

                <div><label className="text-sm font-medium">Descrição</label><input type="text" value={description} onChange={e => setDescription(e.target.value)} className="w-full p-2 mt-1 border rounded" /></div>
                <div><label className="text-sm font-medium">Data</label><input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full p-2 mt-1 border rounded" /></div>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onCancel} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    <button type="submit" disabled={isTransactionLocked} className={`px-4 py-2 font-semibold text-white rounded-md ${buttonClasses[type]} disabled:bg-gray-400 disabled:cursor-not-allowed`}>{isEditing ? 'Guardar Alterações' : 'Lançar'}</button>
                </div>
            </fieldset>
        </form>
    );
});

const TransactionModal = ({ date, transactionToEdit, onFinish, accounts, categories, accountTypes, selectedCurrency, formatCurrency }) => {
    const isEditing = !!transactionToEdit;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                 <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold">{isEditing ? 'Editar Lançamento' : 'Adicionar Lançamento'}</h2>
                    <button onClick={onFinish} className="p-2 rounded-full hover:bg-gray-200"><X /></button>
                </div>
                
                <TransactionForm 
                    selectedDate={date}
                    transactionToEdit={transactionToEdit}
                    accounts={accounts}
                    categories={categories}
                    accountTypes={accountTypes}
                    onFinish={onFinish}
                    onCancel={onFinish}
                    selectedCurrency={selectedCurrency}
                    formatCurrency={formatCurrency}
                />
            </div>
        </div>
    );
};

const TransactionItem = ({ transaction, onEdit, onDelete, isEntityActive, accounts, categories, formatCurrency, selectedCurrency, canEdit, canDelete }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const t = transaction;
    
    // CORREÇÃO: A lógica visual agora depende apenas do `type` da transação.
    const isExpense = t.type === 'expense';
    const amountClass = isExpense ? 'text-red-500' : 'text-green-500';
    const sign = isExpense ? '-' : '+';
    
    const isLinkedTransaction = t.linked_transaction_id;

    const isLocked = useMemo(() => {
        if (isLinkedTransaction) return true;
        const account = accounts.find(acc => acc.id === t.account_id);
        if (account && account.status === 'inativo') return true;
        if (t.to_account_id) {
            const toAccount = accounts.find(acc => acc.id === t.to_account_id);
            if (toAccount && toAccount.status === 'inativo') return true;
        }
        const category = categories.find(cat => cat.name === t.category);
        if (category && category.status === 'inativo') return true;
        return false;
    }, [accounts, categories, t, isLinkedTransaction]);

    return (
        <div className="flex justify-between items-center text-sm p-2 rounded-lg hover:bg-gray-100">
            <div>
                <p className="font-semibold">{t.description}</p>
                <p className="text-xs text-gray-500">{t.category}</p>
            </div>
            <div className="flex items-center gap-2">
                 <p className={`font-semibold ${amountClass}`}>
                    {sign} {formatCurrency(t.amount, selectedCurrency)}
                </p>
                <div className="relative">
                    {/* Botão de menu só aparece se puder editar OU excluir */}
                    {(canEdit || canDelete) && (
                        <button 
                            onClick={() => setIsMenuOpen(!isMenuOpen)} 
                            onBlur={() => setTimeout(() => setIsMenuOpen(false), 200)} 
                            className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!isEntityActive}
                        >
                            <MoreVertical size={16} />
                        </button>
                    )}
                    {isMenuOpen && (
                        <div className="absolute right-0 z-10 w-48 mt-1 bg-white rounded-md shadow-lg">
                            {canEdit && (
                                <button 
                                    onClick={onEdit} 
                                    className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!isEntityActive || isLocked}
                                    title={isLocked ? "Transferências, pagamentos de fatura ou lançamentos com contas/categorias inativas não podem ser editados." : ""}
                                >
                                    <Edit size={14} /> Alterar Lançamento
                                </button>
                            )}
                            {canDelete && (
                                <button 
                                    onClick={onDelete} 
                                    className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-red-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!isEntityActive}
                                >
                                    <Trash2 size={14} /> {isLinkedTransaction ? 'Excluir Par' : 'Excluir Lançamento'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TransactionsPage = ({ 
    transactions, accounts, categories, accountTypes, currentUser, 
    currentMonth, setCurrentMonth, selectedDate, setSelectedDate, 
    selectedUserId, onUserFilterChange, isEntityActive,
    onDataChange,
    selectedCurrency, formatCurrency
}) => {
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // MODIFICADO: Extraindo permissions
    const { apiFetch, permissions } = useApi(); 
    
    const [users, setUsers] = useState([]);
    // Permissões granulares
    const canCreate = permissions?.transactions_create || currentUser?.is_super_user;
    const canEdit = permissions?.transactions_edit || currentUser?.is_super_user;
    const canDelete = permissions?.transactions_delete || currentUser?.is_super_user;

    // CORREÇÃO: Usando a mesma lógica dos relatórios para identificar Super User ou Admin
    const isSuperOrAdmin = currentUser?.is_super_user || permissions?.manageUsers;
    
    useEffect(() => {
        if (isSuperOrAdmin) {
            apiFetch(`/reports/users`).then(res => {
                if (Array.isArray(res)) setUsers(res);
            }).catch(console.error);
        }
    }, [isSuperOrAdmin, apiFetch]);

    const handleDayClick = (day) => {
        setSelectedDate(day);
    };

    const handleOpenModal = (transaction = null) => {
        setEditingTransaction(transaction);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingTransaction(null);
        onDataChange();
    };

    const handleDeleteTransaction = async (transactionId) => {
        if (window.confirm('Tem a certeza que quer excluir este lançamento? Se for uma transferência ou pagamento de fatura, o par correspondente também será excluído.')) {
            try {
                await apiFetch(`/transactions/${transactionId}`, { method: 'DELETE' });
                onDataChange();
            } catch (error) {
                alert(`Erro ao excluir lançamento: ${error.message}`);
            }
        }
    };

    const handlePrevMonth = () => {
        const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
        setCurrentMonth(newDate);
        setSelectedDate(newDate);
    };

    const handleNextMonth = () => {
        const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
        setCurrentMonth(newDate);
        setSelectedDate(newDate);
    };

    const transactionsByDay = useMemo(() => transactions
        .filter(t => !t.is_hidden) // Filtra as transações ocultas
        .reduce((acc, t) => {
            const day = t.date.split('T')[0];
            if (!acc[day]) acc[day] = [];
            acc[day].push(t);
            return acc;
    }, {}), [transactions]);

    const selectedDayDetails = useMemo(() => {
        const dateString = selectedDate.toISOString().split('T')[0];
        // Para os totais, usamos TODAS as transações do dia, mesmo as ocultas
        const allDayTransactions = transactions.filter(t => t.date.startsWith(dateString));
        
        const totalRevenue = allDayTransactions.filter(t => t.type === 'revenue').reduce((sum, t) => sum + t.amount, 0);
        const totalExpenses = allDayTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
        
        // Para a lista, usamos apenas as transações visíveis
        const visibleTransactions = allDayTransactions.filter(t => !t.is_hidden);

        return { transactions: visibleTransactions, revenue: totalRevenue, expenses: totalExpenses };
    }, [selectedDate, transactions]);

    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const selectedDayString = selectedDate.toISOString().split('T')[0];

        const days = [];
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} className="border-r border-b"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateString = date.toISOString().split('T')[0];
            const dayTransactions = transactionsByDay[dateString] || [];
            const hasRevenue = dayTransactions.some(t => t.type === 'revenue');
            const hasExpense = dayTransactions.some(t => t.type === 'expense');
            const isSelected = dateString === selectedDayString;

            days.push(
                <div key={day} className={`relative p-2 border-r border-b cursor-pointer transition-colors ${isSelected ? 'bg-blue-100' : 'hover:bg-gray-50'}`} onClick={() => handleDayClick(date)}>
                    <span className={`font-semibold ${isSelected ? 'text-blue-600' : ''}`}>{day}</span>
                    <div className="absolute bottom-1 right-1 flex gap-1">
                        {hasRevenue && <div className="w-2 h-2 bg-green-500 rounded-full"></div>}
                        {hasExpense && <div className="w-2 h-2 bg-red-500 rounded-full"></div>}
                    </div>
                </div>
            );
        }
        return days;
    };
    
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full max-h-[90vh]">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-4 flex flex-col">
                 <div className="flex items-center justify-between mb-4">
                    <button onClick={handlePrevMonth} className="p-2 rounded-full hover:bg-gray-100"><ArrowLeft /></button>
                    <h2 className="text-2xl font-bold text-center capitalize">{currentMonth.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}</h2>
                    <button onClick={handleNextMonth} className="p-2 rounded-full hover:bg-gray-100"><ArrowRight /></button>
                </div>

                {isSuperOrAdmin && (
                    <div className="mb-4">
                        <label htmlFor="userFilter" className="text-sm font-medium text-gray-700">Filtrar por Utilizador:</label>
                        <select id="userFilter" value={selectedUserId} onChange={e => onUserFilterChange(e.target.value)} className="w-full p-2 mt-1 border border-gray-300 rounded-md">
                            <option value="">Todos os Utilizadores (Unificado)</option>
                            {users.map(user => <option key={user.id} value={user.id}>{user.username}</option>)}
                        </select>
                    </div>
                )}
                
                <div className="grid grid-cols-7 text-center font-semibold text-gray-600">
                    {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => <div key={day} className="py-2 border-b">{day}</div>)}
                </div>
                <div className="grid grid-cols-7 flex-grow" style={{minHeight: '400px'}}>{renderCalendar()}</div>
            </div>

            <div className="flex flex-col bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-4 border-b flex-shrink-0">
                    <h2 className="text-xl font-bold text-gray-800">Lançamentos do Dia</h2>
                    <p className="text-gray-500 text-sm capitalize">{selectedDate.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
                </div>
                <div className="flex-grow overflow-y-auto p-4 space-y-3">
                    {selectedDayDetails.transactions.length > 0 ? (
                        selectedDayDetails.transactions.map(t => (
                            <TransactionItem 
                                key={t.id} 
                                transaction={t} 
                                onEdit={() => handleOpenModal(t)}
                                onDelete={() => handleDeleteTransaction(t.id)}
                                isEntityActive={isEntityActive}
                                accounts={accounts}
                                categories={categories}
                                formatCurrency={formatCurrency}
                                selectedCurrency={selectedCurrency}
                                // Props de permissão adicionadas
                                canEdit={canEdit}
                                canDelete={canDelete}
                            />
                        ))
                    ) : (
                        <div className="flex items-center justify-center h-full text-center text-gray-500">Nenhum lançamento.</div>
                    )}
                </div>
                <div className="p-4 border-t flex-shrink-0">
                     <h3 className="font-bold text-gray-800 mb-2">Resumo do Dia</h3>
                     <div className="flex justify-between text-sm">
                         <span className="text-gray-600">Total de Receitas:</span>
                         <span className="font-semibold text-green-600">{formatCurrency(selectedDayDetails.revenue, selectedCurrency)}</span>
                     </div>
                     <div className="flex justify-between text-sm">
                         <span className="text-gray-600">Total de Despesas:</span>
                         <span className="font-semibold text-red-600">{formatCurrency(selectedDayDetails.expenses, selectedCurrency)}</span>
                     </div>
                     {canCreate && (
                        <button 
                            onClick={() => handleOpenModal()} 
                            className="w-full py-2 mt-4 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            disabled={!isEntityActive}
                            title={!isEntityActive ? "Não é possível adicionar lançamentos a uma entidade inativa." : ""}
                        >
                            Adicionar Lançamento
                        </button>
                     )}
                </div>
            </div>

            {isModalOpen && (
                <TransactionModal 
                    date={selectedDate} 
                    transactionToEdit={editingTransaction}
                    accounts={accounts}
                    categories={categories}
                    accountTypes={accountTypes}
                    onFinish={handleCloseModal}
                    selectedCurrency={selectedCurrency}
                    formatCurrency={formatCurrency}
                />
            )}
        </div>
    );
};

export default TransactionsPage;